// DayDetail — vista completa de un día con mapa + paradas + narrativa
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Navigation, Clock, MapPin, AlertTriangle, Edit3, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { TRIP as STATIC_TRIP } from "../data/trip";
import { resolveStop, formatDate, formatTime, getDayCenter } from "../data/helpers";
import MapView from "../components/MapView";
import { useGeolocation, distanceMeters, formatDistance, walkingMinutes } from "../hooks/useGeolocation";
import { useTickets } from "../hooks/useTickets";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";
import EditSheet, { Field, inputCls } from "../components/EditSheet";
import WeatherBadge from "../components/WeatherBadge";

function uidPlace() { return `place-custom-${Math.random().toString(36).slice(2, 9)}`; }
function emptyPlace(city) {
  return {
    id: uidPlace(), name: "", nameLocal: "", city: city || "", lat: 0, lng: 0,
    type: "highlight", icon: "📍", jewish: false,
    story: "", hours: "", reserve: "", tip: "", noPork: true,
  };
}

export default function DayDetail() {
  const base = useTripBase();
  const { dayId } = useParams();
  const navigate = useNavigate();
  const { trip, tripId, saveTripData } = useTrip();
  const T = trip && trip.days ? trip : {};
  const canEdit = !!tripId;
  const day = T.days.find((d) => d.id === dayId);
  const idx = T.days.findIndex((d) => d.id === dayId);
  const prevDay = idx > 0 ? T.days[idx - 1] : null;
  const nextDay = idx < T.days.length - 1 ? T.days[idx + 1] : null;

  const { position, permission } = useGeolocation();
  const { hasTicket } = useTickets();
  const [activeStop, setActiveStop] = useState(null);
  const [editingPlace, setEditingPlace] = useState(null);
  const [saving, setSaving] = useState(false);

  const stops = useMemo(() => {
    if (!day) return [];
    return (day.stops || []).map((s) => resolveStop(s, T)).filter(Boolean);
  }, [day, T]);

  const mapMarkers = useMemo(() => {
    return stops.map((s) => {
      if (s.kind === "place") {
        const p = s.data;
        const kind = p.type === "synagogue" || p.type === "memorial" && p.jewish ? "jewish"
                    : p.type === "food" ? "food"
                    : p.type === "memorial" ? "jewish"
                    : "";
        return { id: s.id, lat: p.lat, lng: p.lng, emoji: p.icon || "📍", name: p.name, kind };
      }
      if (s.kind === "hotel") {
        return { id: s.id, lat: s.data.lat, lng: s.data.lng, emoji: "🏨", name: s.data.name, kind: "hotel" };
      }
      if (s.kind === "flight") {
        // Mostrar origen del vuelo
        const f = s.data;
        if (!f.from) return null;
        return { id: s.id, lat: f.from.lat, lng: f.from.lng, emoji: "✈️", name: `${f.code} → ${f.to?.city || ""}`, kind: "flight" };
      }
      return null;
    }).filter(Boolean);
  }, [stops]);

  const center = useMemo(() => getDayCenter(day) || [47.4979, 19.0402], [day]);

  if (!day) {
    return (
      <div className="container-app pt-10 pb-28">
        <p>Día no encontrado.</p>
        <Link to={base || "/"} className="btn-ghost mt-4 inline-block">Volver</Link>
      </div>
    );
  }

  const activeData = activeStop ? stops.find((s) => s.id === activeStop) : null;
  const activeCoord = activeData?.kind === "place" || activeData?.kind === "hotel"
    ? { lat: activeData.data.lat, lng: activeData.data.lng }
    : null;
  const routeToActive = position && activeCoord ? { from: position, to: activeCoord } : null;
  const distToActive = position && activeCoord ? distanceMeters(position, activeCoord) : null;

  return (
    <div className="pb-28" data-testid={`day-detail-${day.id}`}>
      {/* Hero */}
      {day.hero && (
        <div className="hero-img relative h-56 sm:h-64">
          <img src={day.hero} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md active:scale-95"
            data-testid="day-back"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <p className="text-xs uppercase tracking-widest opacity-80">
              {formatDate(day.date)} · {day.dayLabel}
            </p>
            <h1 className="text-3xl font-bold leading-tight mt-1">{day.title}</h1>
            <p className="text-sm opacity-95 mt-1">{day.flag} {day.city}</p>
          </div>
        </div>
      )}
      {!day.hero && (
        <div className="container-app pt-6">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-3" data-testid="day-back">
            <ChevronLeft size={16} /> Atrás
          </button>
          <p className="section-title">{formatDate(day.date)} · {day.dayLabel}</p>
          <h1 className="text-3xl font-bold leading-tight">{day.title}</h1>
          <p className="text-sm text-[hsl(var(--muted))] mt-1">{day.flag} {day.city}</p>
        </div>
      )}

      <div className="container-app mt-6">
        {/* Clima del día (forecast 0-16d, histórico >16d) */}
        {center && Array.isArray(center) && day.date && (
          <div className="mb-5" data-testid="day-weather">
            <WeatherBadge
              lat={center[0]}
              lng={center[1]}
              date={day.date}
              city={day.city}
              expanded
            />
          </div>
        )}

        {/* Warning */}
        {day.warning && (
          <div className="card-paper p-4 mb-5 flex gap-3" style={{ background: "hsl(40 95% 96%)", borderColor: "hsl(40 80% 75%)" }}>
            <AlertTriangle size={20} className="text-[hsl(35_85%_40%)] shrink-0 mt-0.5" />
            <p className="text-sm text-[hsl(var(--ink))]">{day.warning}</p>
          </div>
        )}

        {/* Narrativa */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-paper p-5 mb-5"
        >
          <p className="serif text-[17px] leading-[1.65] text-[hsl(var(--ink))]">{day.narrative}</p>
        </motion.section>

        {/* GPS status */}
        {!day.israel && stops.length > 0 && (
          <div className="mb-3 flex items-center gap-2 text-xs">
            {permission === "granted" && position ? (
              <span className="chip-success" data-testid="gps-status">
                <Navigation size={11} /> GPS activo
              </span>
            ) : permission === "denied" ? (
              <span className="chip-warn">📍 GPS denegado — actívalo en Ajustes</span>
            ) : (
              <span className="chip-ink">📍 Esperando GPS...</span>
            )}
          </div>
        )}

        {/* Mapa */}
        {!day.israel && mapMarkers.length > 0 && (
          <div className="card-paper overflow-hidden mb-5" style={{ height: 320 }} data-testid="day-map">
            <MapView
              center={center}
              zoom={13}
              markers={mapMarkers}
              activeId={activeStop}
              userPosition={position}
              routeToActive={routeToActive}
              fitBounds={true}
              height="100%"
              onMarkerClick={(id) => setActiveStop(id)}
            />
          </div>
        )}

        {/* Si tienes activo: distancia */}
        {activeData && distToActive != null && (
          <div className="card-paper p-3 mb-4 flex items-center justify-between" data-testid="day-distance">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Desde tu ubicación</p>
              <p className="text-sm font-medium mt-0.5">
                {activeData.kind === "place" || activeData.kind === "hotel" ? activeData.data.name : "Punto"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-[hsl(var(--accent))]">{formatDistance(distToActive)}</p>
              <p className="text-[11px] text-[hsl(var(--muted))]">≈ {walkingMinutes(distToActive)} min a pie</p>
            </div>
          </div>
        )}

        {/* Lista de paradas */}
        {(stops.length > 0 || canEdit) && (
          <section className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="section-title">Paradas del día</h3>
              {canEdit && (
                <button onClick={() => setEditingPlace({ ...emptyPlace(day.city), _new: true })} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--accent))] active:scale-95" data-testid="add-stop-btn">
                  <Plus size={14} /> Añadir parada
                </button>
              )}
            </div>
            <ol className="space-y-2.5">
              {stops.map((s, i) => (
                <StopCard
                  key={s.id + i}
                  stop={s}
                  trip={T}
                  index={i + 1}
                  active={activeStop === s.id}
                  onSelect={() => setActiveStop(s.id)}
                  userPos={position}
                  hasTicket={hasTicket}
                  dayId={day.id}
                  stopIndex={i}
                  base={base}
                  canEdit={canEdit}
                  onEdit={s.kind === "place" ? () => setEditingPlace(JSON.parse(JSON.stringify(s.data))) : null}
                />
              ))}
            </ol>
          </section>
        )}

        {/* Tips */}
        {day.tips && day.tips.length > 0 && (
          <section className="card-paper p-5 mb-5">
            <h3 className="section-title mb-3">Tips del día</h3>
            <ul className="space-y-2">
              {day.tips.map((t, i) => (
                <li key={i} className="text-sm text-[hsl(var(--ink-soft))] flex gap-2">
                  <span className="text-[hsl(var(--accent))]">·</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Israel placeholder */}
        {day.israel && (
          <div className="card-paper p-6 text-center my-6" style={{ background: "hsl(220 50% 96%)" }}>
            <p className="text-4xl mb-3">🕊️</p>
            <p className="text-sm text-[hsl(var(--ink))]">
              Israel queda libre — están con su hijo. Esta sección la pueden ir llenando ustedes con sus propias notas, fotos y planes.
            </p>
          </div>
        )}

        {/* Nav prev/next */}
        <div className="flex items-center justify-between gap-3 mt-6">
          {prevDay ? (
            <Link to={`${base}/dia/${prevDay.id}`} className="btn-ghost flex-1 flex items-center justify-center gap-1.5" data-testid="day-prev">
              <ChevronLeft size={16} /> Anterior
            </Link>
          ) : <div className="flex-1" />}
          {nextDay ? (
            <Link to={`${base}/dia/${nextDay.id}`} className="btn-primary flex-1 flex items-center justify-center gap-1.5" data-testid="day-next">
              Siguiente <ChevronRight size={16} />
            </Link>
          ) : <div className="flex-1" />}
        </div>
      </div>

      {/* EDIT PLACE SHEET */}
      <EditSheet
        open={!!editingPlace}
        title={editingPlace?._new ? `Añadir parada al día` : `Editar lugar`}
        onClose={() => setEditingPlace(null)}
        onSave={async () => {
          if (!editingPlace) return;
          setSaving(true);
          const { _new, ...clean } = editingPlace;
          clean.lat = Number(clean.lat) || 0;
          clean.lng = Number(clean.lng) || 0;
          const next = JSON.parse(JSON.stringify(T));
          next.places = next.places || {};
          next.places[clean.id] = clean;
          if (_new) {
            next.days = (next.days || []).map((d) => d.id === dayId ? { ...d, stops: [...(d.stops || []), `place:${clean.id}`] } : d);
          }
          try { await saveTripData(next); } finally { setSaving(false); setEditingPlace(null); }
        }}
        onDelete={editingPlace?._new ? null : async () => {
          if (!window.confirm("¿Eliminar este lugar del día?")) return;
          setSaving(true);
          const next = JSON.parse(JSON.stringify(T));
          next.days = (next.days || []).map((d) => d.id === dayId ? { ...d, stops: (d.stops || []).filter((s) => s !== `place:${editingPlace.id}`) } : d);
          try { await saveTripData(next); } finally { setSaving(false); setEditingPlace(null); }
        }}
        saving={saving}
      >
        {editingPlace && (
          <>
            <Field label="Nombre"><input value={editingPlace.name} onChange={(ev) => setEditingPlace({ ...editingPlace, name: ev.target.value })} placeholder="Ej: Plaza Vieja" className={inputCls} data-testid="ep-name" /></Field>
            <Field label="Nombre local (opcional)"><input value={editingPlace.nameLocal || ""} onChange={(ev) => setEditingPlace({ ...editingPlace, nameLocal: ev.target.value })} placeholder="Staroměstské náměstí" className={inputCls} /></Field>
            <Field label="Ciudad"><input value={editingPlace.city} onChange={(ev) => setEditingPlace({ ...editingPlace, city: ev.target.value })} placeholder="Praga" className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitud"><input type="number" step="0.000001" value={editingPlace.lat} onChange={(ev) => setEditingPlace({ ...editingPlace, lat: ev.target.value })} placeholder="50.0875" className={inputCls} /></Field>
              <Field label="Longitud"><input type="number" step="0.000001" value={editingPlace.lng} onChange={(ev) => setEditingPlace({ ...editingPlace, lng: ev.target.value })} placeholder="14.4213" className={inputCls} /></Field>
              <Field label="Tipo">
                <select value={editingPlace.type} onChange={(ev) => setEditingPlace({ ...editingPlace, type: ev.target.value })} className={inputCls}>
                  <option value="highlight">Atracción</option>
                  <option value="synagogue">Sinagoga</option>
                  <option value="memorial">Memorial</option>
                  <option value="museum">Museo</option>
                  <option value="church">Iglesia</option>
                  <option value="food">Restaurante</option>
                  <option value="market">Mercado</option>
                  <option value="park">Parque</option>
                  <option value="viewpoint">Mirador</option>
                </select>
              </Field>
              <Field label="Icono (emoji)"><input value={editingPlace.icon} onChange={(ev) => setEditingPlace({ ...editingPlace, icon: ev.target.value })} placeholder="📍" className={inputCls} /></Field>
            </div>
            <Field label="Historia / descripción"><textarea value={editingPlace.story} onChange={(ev) => setEditingPlace({ ...editingPlace, story: ev.target.value })} placeholder="Lo que hay que saber del lugar…" className={inputCls} rows={4} /></Field>
            <Field label="Horarios"><input value={editingPlace.hours} onChange={(ev) => setEditingPlace({ ...editingPlace, hours: ev.target.value })} placeholder="Lun–Sáb 09:00–18:00" className={inputCls} /></Field>
            <Field label="Reserva / web"><input value={editingPlace.reserve} onChange={(ev) => setEditingPlace({ ...editingPlace, reserve: ev.target.value })} placeholder="+420 ... · ejemplo.com" className={inputCls} /></Field>
            <Field label="Tip"><textarea value={editingPlace.tip} onChange={(ev) => setEditingPlace({ ...editingPlace, tip: ev.target.value })} placeholder="Ir a las 9 sharp..." className={inputCls} rows={2} /></Field>
            <div className="flex items-center gap-4 mb-3 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!editingPlace.jewish} onChange={(ev) => setEditingPlace({ ...editingPlace, jewish: ev.target.checked })} /> Lugar judío
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!editingPlace.noPork} onChange={(ev) => setEditingPlace({ ...editingPlace, noPork: ev.target.checked })} /> Sin cerdo
              </label>
            </div>
          </>
        )}
      </EditSheet>
    </div>
  );
}

function StopCard({ stop, trip, index, active, onSelect, userPos, hasTicket, dayId, stopIndex, base, canEdit, onEdit }) {
  if (stop.kind === "place") {
    const p = stop.data;
    const dist = userPos ? distanceMeters(userPos, { lat: p.lat, lng: p.lng }) : null;
    // Read tickets from the loaded trip first (so server-side updates are reflected),
    // fall back to static for legacy data.
    const ticketsMap = (trip && trip.tickets) || {};
    const ticketInfo = ticketsMap[stop.id];
    const purchased = hasTicket(stop.id) || ticketInfo?.purchased === true;
    return (
      <li>
        <div
          onClick={onSelect}
          className={`card-paper p-4 cursor-pointer transition-all relative ${active ? "ring-2 ring-[hsl(var(--accent))]" : ""}`}
          data-testid={`stop-place-${stop.id}`}
        >
          {canEdit && onEdit && (
            <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[hsl(var(--bg))] border border-[hsl(var(--line))] flex items-center justify-center active:scale-95" aria-label="Editar" data-testid={`edit-stop-${stop.id}`}>
              <Edit3 size={11} />
            </button>
          )}
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-base" style={{ background: "hsl(var(--bg))", border: "1px solid hsl(var(--line))" }}>
              {p.icon || "📍"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">Parada {index}</span>
                {p.jewish && <span className="chip-jewish text-[10px] px-2 py-0.5">✡ Judío</span>}
                {ticketInfo?.status === "advance" && !purchased && <span className="chip-warn text-[10px] px-2 py-0.5">🎟️ Reservar antes</span>}
                {purchased && <span className="chip-success text-[10px] px-2 py-0.5">✓ Ticket</span>}
                {ticketInfo?.status === "free" && <span className="chip-ink text-[10px] px-2 py-0.5">Gratis</span>}
              </div>
              <p className="font-semibold text-[hsl(var(--ink))] leading-tight">{p.name}</p>
              {p.nameLocal && <p className="text-xs text-[hsl(var(--muted))] italic mt-0.5">{p.nameLocal}</p>}
              <p className="text-xs text-[hsl(var(--ink-soft))] mt-1.5 line-clamp-2">{p.story?.slice(0, 140)}{p.story?.length > 140 ? "…" : ""}</p>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-[hsl(var(--muted))]">
                {dist != null && (
                  <span className="flex items-center gap-1"><MapPin size={11} /> {formatDistance(dist)}</span>
                )}
                {p.hours && <span className="flex items-center gap-1"><Clock size={11} /> Horarios</span>}
              </div>
              <Link to={`${base}/lugar/${stop.id}?day=${dayId}&stop=${stopIndex}`} className="text-xs font-medium text-[hsl(var(--accent))] mt-2 inline-flex items-center gap-1" data-testid={`stop-detail-${stop.id}`}>
                Ver detalle <ChevronRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </li>
    );
  }
  if (stop.kind === "hotel") {
    const h = stop.data;
    return (
      <li>
        <Link to={`${base}/logistica`} className="card-paper p-4 flex items-center gap-3 active:scale-[0.98]" data-testid={`stop-hotel-${stop.id}`}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-base" style={{ background: "hsl(var(--gold) / 0.15)", border: "1px solid hsl(var(--gold))" }}>🏨</div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">Hotel · {h.nights} noches</p>
            <p className="font-semibold text-[hsl(var(--ink))] truncate">{h.name}</p>
            <p className="text-xs text-[hsl(var(--muted))] truncate">{h.address}</p>
          </div>
          <ChevronRight size={16} className="text-[hsl(var(--muted))]" />
        </Link>
      </li>
    );
  }
  if (stop.kind === "flight") {
    const f = stop.data;
    return (
      <li>
        <Link to={`${base}/logistica`} className="card-paper p-4 flex items-center gap-3 active:scale-[0.98]" data-testid={`stop-flight-${stop.id}`}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-base" style={{ background: "hsl(var(--ink) / 0.06)", border: "1px solid hsl(var(--ink) / 0.2)" }}>✈️</div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">{f.airline} · {f.code}</p>
            <p className="font-semibold text-[hsl(var(--ink))] truncate">{f.from?.city} → {f.to?.city}</p>
            <p className="text-xs text-[hsl(var(--muted))]">{formatTime(f.depart)} → {formatTime(f.arrive)}</p>
          </div>
          <ChevronRight size={16} className="text-[hsl(var(--muted))]" />
        </Link>
      </li>
    );
  }
  return null;
}
